import {deepCopy} from "../utils/tools";


export const equalFillPortfolio = (tickers, field='wgt') => {
    const numberOfTickers = tickers.length;
    const equalWeightValue = 1 / numberOfTickers;
    return tickers.map(ticker => ({
      ...ticker,
      [field]: equalWeightValue
    }));
}

export const proRataFillPortfolio = (tickers, maxPerHolding=0, field='wgt') => {
    if (!tickers || tickers.length === 0) return [];
    let totalWeight = tickers.reduce((sum, ticker) => sum + parseFloat(ticker[field]), 0);
    let scaleFactor = 1 / totalWeight;

    if (maxPerHolding === 0) {
        return tickers.map(ticker => ({
            ...ticker,
            [field]: parseFloat(ticker[field]) * scaleFactor
        }));
    }
        tickers = tickers.map(ticker => ({
        ...ticker,
        [field]: parseFloat(ticker[field]) * scaleFactor
    }));

    let excessWeight = 0;
    let adjustmentsNeeded;
    do {
        adjustmentsNeeded = false;
        excessWeight = 0;

        tickers = tickers.map(ticker => {
            if (ticker[field] > maxPerHolding) {
                excessWeight += ticker[field] - maxPerHolding;
                adjustmentsNeeded = true;
                return { ...ticker, [field]: maxPerHolding };
            }
            return ticker;
        });

        if (excessWeight > 0) {
            let adjustableTickers = tickers.filter(ticker => ticker[field] < maxPerHolding);
            let adjustableTickersTotalWeight = adjustableTickers.reduce((sum, ticker) => sum + ticker[field], 0);

            if (adjustableTickersTotalWeight === 0) {
                break; // Break the loop if redistribution is not possible.
            }

            const additionalScaleFactor = excessWeight / adjustableTickersTotalWeight;

            tickers = tickers.map(ticker => {
                if (ticker[field] < maxPerHolding) {
                    let additionalWeight = ticker[field] * additionalScaleFactor;
                    if (ticker[field] + additionalWeight > maxPerHolding) {
                        excessWeight += ticker[field] + additionalWeight - maxPerHolding;
                        ticker[field] = maxPerHolding;
                        adjustmentsNeeded = true;
                    } else {
                        ticker[field] += additionalWeight;
                    }
                }
                return ticker;
            });
        }
    } while (adjustmentsNeeded && excessWeight > 0);

    return tickers;

}


export const normalizeWeights = (tickers, field='wgt') => {
    if (!tickers || tickers.length === 0) return [];

    // Calculate the total weight of all tickers
    const totalWeight = tickers.reduce((sum, ticker) => sum + parseFloat(ticker[field]), 0);

    if (totalWeight === 0) {
        // In case all weights are zero, distribute evenly as a fallback
        const equalWeightValue = 1 / tickers.length;
        return tickers.map(ticker => ({
            ...ticker,
            [field]: equalWeightValue
        }));
    }

    // Scale each weight to normalize to 1
    const normalizationFactor = 1 / totalWeight;
    return tickers.map(ticker => ({
        ...ticker,
        [field]: parseFloat(ticker[field]) * normalizationFactor
    }));
};


export const scalePortfolioToTarget = (tickers, scaleFactor, field='wgt') => {
    if (!tickers || tickers.length === 0) return [];

    // Apply the scale factor to each ticker's weight
    tickers = tickers.map(ticker => ({
        ...ticker,
        [field]: parseFloat(ticker[field]) * scaleFactor
    }));

    return tickers;
};


let _listToMapping = (list)=>{
    let mapping = {};
    list.forEach(item=>{
        mapping[item.symbol] = item;
    });
    return mapping;
}



class SectorReallocationScheme {

    constructor(subset, otherSet, sectorThreshold, ignoreReweightThreshold=0.1) {
        this.subset = deepCopy(subset); // Ensure deepCopy function is implemented to handle deep cloning
        this.otherSet = deepCopy(otherSet);
        this.sectorThreshold = sectorThreshold;
        this.ignoreReweightThreshold = ignoreReweightThreshold;
        this.mapping = _listToMapping([...this.subset, ...this.otherSet]);

        // Object to track the original and adjusted weights for each ticker
        this.weightChanges = {};
        this._initializeWeightChanges();
    }

    // Initialize the weight changes tracker with original weights
    _initializeWeightChanges() {
        [...this.subset, ...this.otherSet].forEach(ticker => {
            this.weightChanges[ticker.symbol] = {
                originalWeight: ticker.wgt,
                newWeight: ticker.wgt,
                percentChange: 0
            };
        });
    }
    _updateWeightChange(symbol, newWeight) {
        const originalWeight = this.weightChanges[symbol].originalWeight;

        // Only update if the final weight is actually different from the original weight
        if (newWeight !== originalWeight) {
            this.weightChanges[symbol].newWeight = newWeight;
            this.weightChanges[symbol].percentChange = ((newWeight - originalWeight) / originalWeight) * 100;
        } else {
            this.weightChanges[symbol].percentChange = 0;  // No change if weights are equal
        }
    }

    _getByTag(tickerSet, matchTag){
        return tickerSet.reduce((acc, item) => {
            let tag = item?.[matchTag] ?? 'missing';
            if (!acc[tag]) {
                acc[tag] = {
                    count: 0,
                    tickers: [],
                    wgt: 0
                };
            }
            acc[tag].count += 1;
            acc[tag].tickers.push(item.symbol);
            acc[tag].wgt += item.wgt;
            return acc;
        }, {});
    }

    /**
     * Get the sector adjusted portfolio list (cutoff subset + needed otherSet tickers to meet sector threshold)
     * @returns {*[]}
     */
    getAdjustedPortfolioList() {
    // Object to track the adjusted list of tickers for each sector
        const nextOutWeights = {};
        let addedTickers = [];
        let portfolio = [...deepCopy(this.subset)];

        // Aggregate sector weights in otherSet
        const otherSetSectorWeights = this._getByTag(this.otherSet, 'sector');
        // Iterate through each sector in the otherSet
        for (let sector in otherSetSectorWeights) {
            const sectorData = otherSetSectorWeights[sector];
            const sectorWeight = sectorData.wgt;
            const tickers = sectorData.tickers;
            /**
             * If the sector weight - sectorThreshold is greater than 0, we will continue with
             * ... the reallocation process. Otherwise, we will skip the sector because its in line within
             * ... the sectorThreshold tolerance
             */
            if (tickers.length > 0 && sectorWeight - this.sectorThreshold > 0) {
                let adjustedSectorWeight = sectorWeight - this.sectorThreshold;
                /**
                 * Finding the weight of the first ticker in the out set is critical here for correctly ensuring
                 * the weights of tickers added back in remain in line with the smallest weights currently within
                 * subset.
                 */
                const firstTickerWeight = this.mapping[tickers[0]].wgt;
                let hasRemainder = false;
                // Determine number of tickers needed to match the sector weight
                let tickersNeeded = Math.round(adjustedSectorWeight / firstTickerWeight);
                let remainderWeight = tickersNeeded * firstTickerWeight - adjustedSectorWeight;

                if (remainderWeight > 0 && tickersNeeded < tickers.length) {
                    /**
                     * If there is a remainder weight and the number of tickers needed is less than the total
                     * ... number of tickers in the sector, we will add 1 to the number of tickers needed
                     */
                    tickersNeeded++;
                    hasRemainder = true;
                }
                const selectedTickers = tickers.slice(0, tickersNeeded);  // Select tickers to include
                nextOutWeights[sector] = {
                    tickers: selectedTickers,
                    equalWeight: firstTickerWeight,   // Equal weight of each ticker
                    remainderWeight: remainderWeight,   // Remainder weight of the sector for +1 ticker
                    remainderTicker: hasRemainder === true ? selectedTickers[selectedTickers.length - 1] : null
                };
                addedTickers.push(...selectedTickers.map(x=>x));
            }
        }

        // Build the final portfolio
        for (let sector in nextOutWeights) {
            let [tickers, equalWeight, remainderWeight, remainderTicker] = [
                nextOutWeights[sector].tickers,
                nextOutWeights[sector].equalWeight,
                nextOutWeights[sector].remainderWeight,
                nextOutWeights[sector].remainderTicker
            ];
            tickers.forEach(ticker => {
                let obj = this.mapping[ticker];
                obj.sectorRefill = addedTickers.includes(ticker);
                obj.isSectorRemainder = false;
                if (obj.sectorRefill===true){
                    if (ticker != remainderTicker) {
                        obj.wgt = equalWeight;
                        this._updateWeightChange(ticker, equalWeight);  // Track the weight change

                    }
                    else {
                        obj.wgt = remainderWeight;
                        obj.isSectorRemainder = true;
                        this._updateWeightChange(ticker, remainderWeight);  // Track the weight change

                    }
                }
                portfolio.push(obj);
            });
        }
        return portfolio;
    }

    reallocateSectorsToSubset(adjustedPortfolioList, remainderSectorWeights) {
        let copiedAdjustedPortfolioList = deepCopy(adjustedPortfolioList);

        for (let sector in remainderSectorWeights) {
            let remainderWeight = remainderSectorWeights[sector];
            if (remainderWeight > 0) {
                let tickersInSector = copiedAdjustedPortfolioList.filter(x => x?.sector === sector);
                let wgtPerTicker = remainderWeight / tickersInSector.length;

                tickersInSector.forEach(x => {
                    copiedAdjustedPortfolioList.find(y => y.symbol === x.symbol).wgt += wgtPerTicker;
                    this._updateWeightChange(x.symbol, copiedAdjustedPortfolioList.find(y => y.symbol === x.symbol).wgt);  // Track weight change
                });
            }
        }
        return copiedAdjustedPortfolioList;
    }
    /**
    * Get the sector weights for everything not in adjustedPortfolioList
    * @param adjustedPortfolioList
    * @returns {{}}
    */
    getRemainderSectorWeights(adjustedPortfolioList) {
        let totalAdjustedWeight = adjustedPortfolioList.reduce((acc, item) => acc + item.wgt, 0);
        let sectorWeights = {};
        let uniqueSymbols = adjustedPortfolioList.map(x => x.symbol);
        this.otherSet.forEach(item => {
            if (!uniqueSymbols.includes(item.symbol)) {
                let sector = item?.sector ?? 'missing';
                if (!sectorWeights[sector]) {
                    sectorWeights[sector] = 0;
                }
                sectorWeights[sector] += item.wgt;
            }
        });

        // Normalize the sector weights based on the total adjusted weight
        for (let sector in sectorWeights) {
            sectorWeights[sector] = sectorWeights[sector] / totalAdjustedWeight;
        }

        return sectorWeights;
    }
}


export const sectorTargetFillPortfolio = (subset, outset, sectorTolerance = 0.02) => {
    let sectorScheme = new SectorReallocationScheme(subset, outset, sectorTolerance);
    let adjustedPortfolioList = sectorScheme.getAdjustedPortfolioList();
    let remainderSectorWeights = sectorScheme.getRemainderSectorWeights(adjustedPortfolioList);
    let reallocatedPortfolioList = sectorScheme.reallocateSectorsToSubset(adjustedPortfolioList, remainderSectorWeights);
    const proRataFilled = proRataFillPortfolio(reallocatedPortfolioList);
    return [proRataFilled, remainderSectorWeights, sectorScheme.weightChanges];
}

